import {
  Color,
  Mesh,
  NormalBlending,
  PlaneGeometry,
  ShaderMaterial,
} from 'three';
import { Metrics } from '../_app/cuchillo/core/Metrics';

// Boirina de l'horitzó: una franja de boira densa a la LÍNIA DE L'HORITZÓ que s'esvaeix cap amunt i
// cap avall, de manera que el mar+cel llunyans no es vegin bé allà. És un pla gran semitransparent,
// davant la càmera, centrat a l'alçada de l'ull (horitzó = centre de pantalla en camera2D). Autònom;
// Scene el munta al SEA_LAYER (render de dalt). Cicle de vida: init/loop/setConfig/setLayer/dispose.
//
// NO és fog d'escena ni toca el compositor: només un quad amb un degradat vertical (+ una lleugera
// variació de noise perquè no sigui una franja perfectament plana).
export const HAZE_SETTINGS = {
  color: '#c9d6e6',   // color de la boira
  density: 0.72,      // opacitat màxima a l'horitzó
  band: 0.12,         // mig-amplada de la franja (fracció del pla, 0..1)
  softness: 0.30,     // suavitat del degradat vertical (com s'esvaeix amunt/avall)
  speed: 0.05,        // deriva de la variació de noise
  // RESPONSIVE: mides/posicions en FRACCIÓ de pantalla × Metrics.WIDTH/HEIGHT al punt d'ús (ref
  // 1440×900). SCALE = × WIDTH (amplada/distància); VERT = × HEIGHT (alçada/posició vertical).
  offsetY: 0.75,       // VERT  · ≈675px @900 · desplaçament vertical del centre de la franja (0 = horitzó)
  sizeX: 12.5,         // SCALE · ≈18000px @1440 · amplada del pla
  sizeY: 6.0,          // VERT  · ≈5400px @900 · alçada del pla
  distance: 4.513889,  // SCALE · ≈6500px @1440 · distància davant la càmera
};

const VERTEX = /* glsl */`
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const FRAGMENT = /* glsl */`
  precision highp float;
  varying vec2 vUv;
  uniform vec3 uColor;
  uniform float uTime, uDensity, uBand, uSoftness, uSpeed;

  float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    float a = hash(i), b = hash(i + vec2(1.0, 0.0));
    float c = hash(i + vec2(0.0, 1.0)), d = hash(i + vec2(1.0, 1.0));
    return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
  }

  void main() {
    // Distància vertical al centre (horitzó): 0 al centre → 1 a les vores.
    float v = abs(vUv.y - 0.5) * 2.0;
    // Franja densa al centre que s'esvaeix cap amunt i cap avall.
    float band = 1.0 - smoothstep(uBand, uBand + uSoftness, v);
    // Variació horitzontal suau perquè la boira no sigui una franja plana.
    float n = 0.82 + 0.18 * vnoise(vec2(vUv.x * 6.0 + uTime * uSpeed, vUv.y * 3.0));
    float a = band * uDensity * n;
    if (a <= 0.003) discard;
    gl_FragColor = vec4(uColor, a);
  }
`;

export default class HorizonHaze {
  constructor(opts = {}) {
    if (opts.settings) Object.assign(HAZE_SETTINGS, opts.settings);
    this.mesh = null;
    this.material = null;
  }

  init() {
    const s = HAZE_SETTINGS;
    this.material = new ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uColor: { value: new Color(s.color) },
        uDensity: { value: s.density },
        uBand: { value: s.band },
        uSoftness: { value: s.softness },
        uSpeed: { value: s.speed },
      },
      vertexShader: VERTEX,
      fragmentShader: FRAGMENT,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blending: NormalBlending,
    });
    const W = Metrics.WIDTH, H = Metrics.HEIGHT;
    this.mesh = new Mesh(new PlaneGeometry(s.sizeX * W, s.sizeY * H), this.material);
    this.mesh.position.set(0, s.offsetY * H, -s.distance * W); // fraccions × pantalla (responsive)
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 4; // per sobre del mar/cel llunyà → l'enterboleix
    return this.mesh;
  }

  loop(dt = 0.016) {
    if (this.material) this.material.uniforms.uTime.value += dt;
  }

  setConfig(partial = {}) {
    Object.assign(HAZE_SETTINGS, partial);
    const s = HAZE_SETTINGS;
    if (this.material) {
      const u = this.material.uniforms;
      u.uColor.value.set(s.color);
      u.uDensity.value = s.density;
      u.uBand.value = s.band;
      u.uSoftness.value = s.softness;
      u.uSpeed.value = s.speed;
    }
    this._applySize();
  }

  setLayer(n) { if (this.mesh) this.mesh.layers.set(n); }

  // Ara SÍ depèn de la mida de pantalla: reconstrueix el pla i reposiciona (fraccions × Metrics).
  resize() { this._applySize(); }

  _applySize() {
    if (!this.mesh) return;
    const s = HAZE_SETTINGS, W = Metrics.WIDTH, H = Metrics.HEIGHT;
    this.mesh.geometry.dispose();
    this.mesh.geometry = new PlaneGeometry(s.sizeX * W, s.sizeY * H);
    this.mesh.position.set(0, s.offsetY * H, -s.distance * W);
  }

  dispose() {
    this.mesh?.geometry?.dispose();
    this.material?.dispose();
    this.mesh = null;
    this.material = null;
  }

  get settings() { return HAZE_SETTINGS; }
}
